import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  ResolveFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { catchError, map, of, tap } from 'rxjs';

import { CourtService } from '@shared/services/court.service';
import { StorageService } from '@shared/services/storage.service';

export const courtResolver: ResolveFn<boolean> = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const router = inject(Router);
  const courtService = inject(CourtService);
  const storage = inject(StorageService);

  const stateParam = getStateParam(route);

  // Multi-county falls back to the state root, which renders the county picker
  // — a route with no courtResolver, so a failure there settles. Single-county
  // routes carry no :state param and have no such safe harbour: every route is
  // behind this resolver, so redirecting on failure would loop. Cancel instead
  // and let the navigation error surface.
  const bailOut = (): false => {
    if (stateParam) {
      router.navigateByUrl(`/${stateParam}`, { replaceUrl: true });
    }

    return false;
  };
  const url = (state?.url || '').toLowerCase();
  const isStartFlow =
    !!stateParam && (url.includes('/start') || url.includes('/invitation'));

  // Start flows (including invitation deep links) should be reachable without a selected court.
  // Without this, links like /:state/probate/start/invitation?token=... will always redirect to
  // the county picker because courtId isn't stored yet.
  if (!courtService.courtId() && isStartFlow) {
    return of(true);
  }
  const state_geoname_code = courtService.court().state_geoname_code;

  if (stateParam && state_geoname_code && stateParam !== state_geoname_code) {
    storage.clear();
    return bailOut();
  }

  return courtService.getCourt().pipe(
    tap((success) => {
      if (!success) {
        console.error('courtResolver: court could not be loaded', {
          operation: 'courtResolver',
          courtId: courtService.courtId(),
          stateParam,
          url,
        });
        bailOut();
      }
    }),
    map((success) => !!success),
    catchError((error) => {
      console.error('Error in courtResolver:', {
        operation: 'courtResolver',
        courtId: courtService.courtId(),
        stateParam,
        url,
        err: error,
      });
      return of(bailOut());
    }),
  );
};

const getStateParam = (route: ActivatedRouteSnapshot): string | undefined => {
  let current: ActivatedRouteSnapshot | null = route;
  while (current) {
    const state = current.paramMap.get('state');
    if (state) return state.toUpperCase();
    current = current.parent;
  }
  return undefined;
};
